import { NAME } from '@app/account/constants';

// Utils
import * as EmailValidator from 'email-validator';
import { estimatePasswordComplexity } from '../../utils';
import { getCurrencyFromPlanId } from '@app/pricing/helper';

// Hooks
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'next-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrencyValue } from '@app/pricing/hooks/useCurrencyValue';
import { useValidatePartnership } from '@app/account/hooks/useValidatePartnership';

// UI
import Link from 'next/link';
import { Field, Form } from 'react-final-form';
import Emoji from '@ui/components/Emoji';
import TextInput from '@ui/components/TextInput';
import Checkbox from '@ui/components/Checkbox';
import ButtonLoadingSpinner from '@ui/components/ButtonLoadingSpinner';
import RevealPasswordEye from '@ui/components/RevealPasswordEye';
import { CheckIcon } from '@heroicons/react/24/solid';
import { CircularProgressbarWithChildren } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

// Redux
import { validateCoupon } from '@app/account/models/coupon';
import { handleSetCurrency } from '@app/pricing/models/pricing';
import { getLoading, getError, createAccount } from '../../models/signup';

// Types
import type { FC } from 'react';
import type { SignupValidationErrors, SignupFormValues } from '../../types';

export interface Props {
    loginUrl?: string;
    successRedirectUrl?: string;
    fixedPlanId?: string;
    buttonText?: string;
    consentTransKey?: string;
}

const SignupForm: FC<Props> = ({
    loginUrl,
    successRedirectUrl,
    fixedPlanId,
    buttonText,
    consentTransKey,
}) => {
    const { t, i18n } = useTranslation(NAME);

    const [isPasswordFieldEmpty, setIsPasswordFieldEmpty] = useState<boolean>(true);
    const [passwordStrength, setPasswordStrength] = useState<number>(0);
    const [revealPassword, setRevealPassword] = useState<boolean>(false);

    const loading = useSelector(getLoading);
    const error = useSelector(getError);

    const dispatch = useDispatch();
    const router = useRouter();
    const { query, locale } = router;
    const { coupon, planId, partnerKey } = query;

    const { symbol: currSymbol } = useCurrencyValue();

    useValidatePartnership();

    useEffect(() => {
        if (coupon) {
            dispatch(validateCoupon(coupon as string));
        }
    }, [dispatch, coupon]);

    // Handle set currency from query plan id
    useEffect(() => {
        if (planId) {
            const currencyFromQuery = getCurrencyFromPlanId(planId as string);

            dispatch(handleSetCurrency(currencyFromQuery));
        }
    }, [dispatch, planId]);

    const togglePassword = () => {
        setRevealPassword(!revealPassword);
    };

    const validate = (values: SignupFormValues): SignupValidationErrors => {
        const errors: SignupValidationErrors = {};

        if (!values.name?.trim()) {
            errors.name = 'required';
        }
        if (!values.email) {
            errors.email = 'required';
        } else if (!EmailValidator.validate(values.email?.trim())) {
            errors.email = 'invalid-email';
        }
        if (!values.password) {
            errors.password = 'required';
        }
        if (!values.consent) {
            errors.consent = 'required';
        }
        if (values.password && values.password?.length > 128) {
            errors.password = 'too-long';
        }

        return errors;
    };

    const onSubmit = async (values: SignupFormValues) => {
        const account = {
            name: values.name,
            email: values.email?.trim(),
            password: values.password,
        };

        dispatch(
            createAccount({
                account,
                query: {
                    ...query,
                    planId: fixedPlanId ? fixedPlanId : (planId as string),
                },
                locale,
                redirectUrl: successRedirectUrl,
            }),
        );
    };

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const password = event?.target?.value;

        if (!password) {
            setPasswordStrength(0);
            setIsPasswordFieldEmpty(true);
        } else {
            const strengthLevel = estimatePasswordComplexity(password);
            setPasswordStrength(strengthLevel || 0);
            setIsPasswordFieldEmpty(false);
        }
    };

    return (
        <>
            <div className="mx-auto w-full sm:w-96">
                <Form
                    onSubmit={onSubmit}
                    validate={validate}
                    render={({ handleSubmit }) => (
                        <form onSubmit={handleSubmit} data-cy="signup-form">
                            <div className="divide-y rounded-xl border bg-white shadow">
                                <Field
                                    name="name"
                                    component={TextInput}
                                    placeholder={t('placeholder-name')}
                                    icon="👋"
                                />

                                <Field
                                    name="email"
                                    component={TextInput}
                                    placeholder={t('placeholder-business-email')}
                                    icon="✉️"
                                />

                                <Field
                                    name="password"
                                    component={TextInput}
                                    type={revealPassword ? 'text' : 'password'}
                                    placeholder={t('placeholder-password')}
                                    onChange={handlePasswordChange}
                                    icon={
                                        <>
                                            {isPasswordFieldEmpty && (
                                                <Emoji
                                                    symbol="🔐"
                                                    className="pt-0.5 text-[20px] leading-[20px]"
                                                />
                                            )}
                                            <CircularProgressbarWithChildren
                                                className={isPasswordFieldEmpty && 'invisible'}
                                                value={
                                                    isPasswordFieldEmpty ? 0 : passwordStrength + 1
                                                }
                                                minValue={0}
                                                maxValue={5}
                                                strokeWidth={16}
                                                styles={{
                                                    path: {
                                                        // Path color map
                                                        stroke: {
                                                            0: '#ef4444', // Red
                                                            1: '#ef4444', // Red
                                                            2: '#fb923c', // Orange
                                                            3: '#fdbb74', // Yellow
                                                            4: '#83cc16', // Lime-Green
                                                            5: '#22c55e', // Green
                                                        }[passwordStrength + 1],
                                                        transition:
                                                            'stroke 1s ease-out 0.2s, stroke-dashoffset 1s ease-out 0.2s',
                                                    },
                                                }}
                                            >
                                                {!isPasswordFieldEmpty && (
                                                    <span>
                                                        <CheckIcon
                                                            className={`h-3 w-3 text-green-600 transition-opacity delay-200 duration-1000 ${
                                                                passwordStrength === 4
                                                                    ? 'opacity-1'
                                                                    : 'opacity-0'
                                                            }`}
                                                        />
                                                        <p
                                                            className={`absolute bottom-[-80%] left-[50%] w-fit translate-x-[-50%] text-xs text-green-600 transition delay-200 duration-1000 ${
                                                                passwordStrength === 4
                                                                    ? 'opacity-1 translate-y-0'
                                                                    : 'translate-y-[-70%] opacity-0'
                                                            }`}
                                                        >
                                                            {t('strong')}
                                                        </p>
                                                    </span>
                                                )}
                                            </CircularProgressbarWithChildren>
                                        </>
                                    }
                                >
                                    <RevealPasswordEye
                                        togglePassword={togglePassword}
                                        revealPassword={revealPassword}
                                    />
                                </Field>

                                <Field
                                    id="consent"
                                    name="consent"
                                    component={Checkbox}
                                    type="checkbox"
                                    className="px-6 py-4"
                                >
                                    <Trans
                                        i18nKey={consentTransKey || `${NAME}:consent`}
                                        values={{ currency: currSymbol }}
                                        components={{
                                            tos: (
                                                <a
                                                    href={t('common:tos-link-href')}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="underline"
                                                />
                                            ),
                                            privacy: (
                                                <a
                                                    href={t('common:privacy-link-href')}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="underline"
                                                />
                                            ),
                                        }}
                                    />
                                </Field>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="mt-4 w-full rounded-xl bg-blue-500 p-5 text-white transition-colors hover:bg-blue-600"
                                data-cy="submit-form"
                            >
                                {loading ? <ButtonLoadingSpinner /> : buttonText || t('next-step')}
                            </button>
                        </form>
                    )}
                />
                {error && (
                    <div
                        className="mt-4 flex items-center rounded-xl bg-red-50 p-5 text-sm text-red-500"
                        data-cy="response-error"
                    >
                        <span className="ml-2">
                            {error?.statusCode && i18n.exists(`${NAME}:signup-${error.statusCode}`)
                                ? t(`signup-${error.statusCode}`)
                                : t('signup-generic')}
                        </span>
                    </div>
                )}
            </div>

            <div className="mt-6 flex items-center justify-center text-sm text-gray-500">
                <span className="pl-2">
                    {t('no-account-login-text')}{' '}
                    <Link
                        href={{
                            pathname: loginUrl || '/login',
                            query,
                        }}
                        passHref
                        data-cy="login-link"
                    >
                        <p className="inline cursor-pointer underline transition-colors hover:text-blue-500">
                            {t('login')}
                        </p>
                    </Link>
                </span>
            </div>
        </>
    );
};

export default SignupForm;
